import { Icon } from 'idea-react';
import { observable } from 'mobx';
import { observer } from 'mobx-react';
import { Component } from 'react';
import { Badge, Button, ProgressBar } from 'react-bootstrap';

import { t } from '../model/Translation';

export interface CategorySuggestion {
    name: string;
    description: string;
    matchingOrders: number;
    confidence: number;
    isNew: boolean;
}

export interface AIClassifierProps {
    onComplete?: () => void;
    onCancel?: () => void;
}

@observer
export class AIClassifier extends Component<AIClassifierProps> {
    @observable
    accessor aiProgress = 0;

    @observable
    accessor aiStage: 'analyzing' | 'suggestions' = 'analyzing';

    @observable
    accessor suggestions: CategorySuggestion[] = [];

    private intervalId?: NodeJS.Timeout;

    componentDidMount() {
        this.startAnalysis();
    }

    componentWillUnmount() {
        this.clearAnalysis();
    }

    startAnalysis() {
        this.aiProgress = 0;
        this.aiStage = 'analyzing';
        this.suggestions = [];

        this.intervalId = setInterval(() => {
            this.aiProgress += 5;

            if (this.aiProgress < 100) return;

            this.clearAnalysis();
            this.aiProgress = 45;
            this.aiStage = 'suggestions';
            this.suggestions = [
                {
                    name: '新能源设备安装',
                    description: '光伏发电设备、储能设备等新能源设备安装需求',
                    matchingOrders: 156,
                    confidence: 92,
                    isNew: true
                },
                {
                    name: '智能电表问题',
                    description: '智能电表安装、显示异常等相关问题',
                    matchingOrders: 89,
                    confidence: 88,
                    isNew: true
                }
            ];
        }, 200);
    }

    clearAnalysis() {
        if (!this.intervalId) return;

        clearInterval(this.intervalId);
        this.intervalId = undefined;
    }

    renderAnalyzingStage = () => (
        <div className="p-4 bg-primary bg-opacity-10 rounded">
            <div className="text-center mb-4">
                <div
                    className="d-inline-flex align-items-center justify-content-center mb-3"
                    style={{
                        width: '80px',
                        height: '80px',
                        backgroundColor: '#2196F3',
                        borderRadius: '12px'
                    }}
                >
                    <Icon className="text-white" name="stars" size={3} />
                </div>
                <h5 className="mb-2">{t('ai_classifying')}</h5>
                <p className="text-muted">{t('analyzing_data')}</p>
            </div>
            <div className="mb-3">
                <ProgressBar
                    now={this.aiProgress}
                    variant="primary"
                    style={{ height: '8px' }}
                />
            </div>
            <div className="text-center text-muted">
                <small>
                    {t('analyzing_orders')} {this.aiProgress}%
                </small>
            </div>
        </div>
    );

    renderSuggestionItem = ({
        name,
        description,
        isNew,
        matchingOrders,
        confidence
    }: CategorySuggestion) => (
        <li key={name} className="border rounded p-3 mb-3">
            <div className="d-flex justify-content-between align-items-start mb-2">
                <div>
                    <h6 className="mb-1">
                        {name}
                        {isNew && (
                            <Badge bg="success" className="ms-2">
                                {t('new_category_tag')}
                            </Badge>
                        )}
                    </h6>
                    <p className="text-muted mb-2 small">{description}</p>
                </div>
            </div>
            <div className="d-flex justify-content-between align-items-center">
                <div className="small text-muted">
                    <span className="me-3">
                        {t('matching_orders')}
                        <strong className="text-primary ms-1">
                            {matchingOrders}
                        </strong>
                    </span>
                    <span>
                        {t('confidence')}
                        <strong className="text-primary ms-1">
                            {confidence}%
                        </strong>
                    </span>
                </div>
                <div>
                    <Button size="sm" variant="outline-danger" className="me-2">
                        {t('reject')}
                    </Button>
                    <Button size="sm" variant="success">
                        {t('accept')}
                    </Button>
                </div>
            </div>
        </li>
    );

    renderSuggestionsStage = () => (
        <div>
            <div>
                <h6 className="my-3">{t('new_category_suggestions')}</h6>
                <ol className="list-unstyled">
                    {this.suggestions.map(this.renderSuggestionItem)}
                </ol>
            </div>
            <footer className="d-flex justify-content-between align-items-center border-top">
                <div className="text-muted small text-center mt-3">
                    {t('last_update_time')}: 2024-01-15 14:30
                </div>
                <div className="d-flex justify-content-end gap-2 mt-4 pt-3">
                    <Button variant="secondary" onClick={this.props.onCancel}>
                        {t('close')}
                    </Button>
                    <Button variant="primary" onClick={this.props.onComplete}>
                        {t('confirm_update')}
                    </Button>
                </div>
            </footer>
        </div>
    );

    render() {
        return (
            <>
                {this.renderAnalyzingStage()}

                {this.aiStage === 'suggestions' &&
                    this.renderSuggestionsStage()}
            </>
        );
    }
}
